// FILE: src/components/student/LessonList.jsx
// The student's chapters or homelinks as a list of rows: number, title,
// status (icon + words, never colour alone — MTO 5.4), slide progress and
// the action. Lessons are disabled when the server would refuse them
// (daily cap, break, enrolment ended — WP-3); the page writes the reason
// out once (BLOCKED_REASON) and passes its id as blockedReasonId. Practice
// activities are labelled as such: never scored, never blocking (MTO
// glossary, matrix 3.7).
import React from "react";
import { Box, Button, LinearProgress, Stack, Typography } from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import { useNavigate } from "react-router-dom";
import { StatusChip } from "../ui";
import { STATUS_LABEL, actionLabel, lessonPercent } from "./lessons";

const STATUS_STYLE = {
  completed: { tone: "success", icon: <CheckCircleOutlineIcon /> },
  "in-progress": { tone: "info", icon: <PlayCircleOutlineIcon /> },
  "not-started": { tone: "default", icon: <RadioButtonUncheckedIcon /> },
  locked: { tone: "default", icon: <LockOutlinedIcon /> }
};

export const BLOCKED_REASON = {
  cap: "Lessons are closed for today: you've reached the 5-hour daily limit. They open again after midnight Ontario time.",
  break: "Lessons open again when your 15-minute break is over.",
  expired: "Your enrolment has ended, so lessons are closed."
};

function formatDate(value) {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString("en-CA", { year: "numeric", month: "short", day: "numeric" });
}

export default function LessonList({ rows, kind = "chapter", blocked, untimed = false, blockedReasonId }) {
  const navigate = useNavigate();
  const typeParam = kind === "homelink" ? "homelink" : "chapter";

  return (
    <Box>
      <Stack component="ol" spacing={1.5} sx={{ listStyle: "none", m: 0, p: 0 }}>
        {rows.map((row, i) => {
          const status = row.status;
          const style = STATUS_STYLE[status];
          const canOpen = row.unlocked || untimed;
          const pct = lessonPercent(row);
          const title = row.title || `${kind === "homelink" ? "Homelink" : "Chapter"} ${i + 1}`;
          const done = formatDate(row.completedAt);
          return (
            <Box
              component="li"
              key={row._id || i}
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "auto 1fr", md: "auto minmax(0, 1fr) 200px auto" },
                gap: { xs: 1.5, md: 2.5 },
                alignItems: "center",
                p: { xs: 2, md: 2.5 },
                borderRadius: 3,
                border: "1px solid",
                borderColor: status === "in-progress" ? "primary.main" : "divider",
                bgcolor: "background.paper",
                opacity: canOpen ? 1 : 0.85
              }}
            >
              <Box
                aria-hidden
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: 2.5,
                  display: "grid",
                  placeItems: "center",
                  fontWeight: 700,
                  fontSize: "1.0625rem",
                  bgcolor: status === "completed" ? "primary.main" : "zun.primaryTint",
                  color: status === "completed" ? "primary.contrastText" : "primary.main"
                }}
              >
                {status === "completed" ? <CheckCircleOutlineIcon /> : status === "locked" ? <LockOutlinedIcon /> : i + 1}
              </Box>

              <Box sx={{ minWidth: 0 }}>
                <Typography component="h3" sx={{ fontWeight: 650, fontSize: "1.0625rem", overflowWrap: "anywhere" }}>
                  {title}
                </Typography>
                <Stack direction="row" spacing={1} sx={{ mt: 0.75, alignItems: "center", flexWrap: "wrap", rowGap: 0.75 }}>
                  <StatusChip label={STATUS_LABEL[status]} tone={style.tone} icon={style.icon} size="small" />
                  {row.isPractice && (
                    <StatusChip label="Practice, not scored" tone="info" icon={<SchoolOutlinedIcon />} size="small" data-testid="practice-label" />
                  )}
                  {status === "locked" && (row.prerequisite?.title || i > 0) && (
                    <Typography sx={{ color: "text.secondary" }}>
                      Finish {row.prerequisite?.title || rows[i - 1]?.title || "the previous lesson"} first
                    </Typography>
                  )}
                  {done && status === "completed" && <Typography sx={{ color: "text.secondary" }}>Finished {done}</Typography>}
                  {row.finalScore > 0 && !row.isPractice && <Typography sx={{ color: "text.secondary" }}>Quiz {row.finalScore}%</Typography>}
                </Stack>
              </Box>

              <Box sx={{ gridColumn: { xs: "1 / -1", md: "auto" } }}>
                <LinearProgress
                  variant="determinate"
                  value={pct}
                  aria-label={`${title} progress`}
                  aria-valuetext={`${row.completedSlides} of ${row.totalSlides} slides`}
                />
                <Typography sx={{ color: "text.secondary", mt: 0.75 }}>
                  {row.completedSlides} of {row.totalSlides} slides
                </Typography>
              </Box>

              <Box sx={{ gridColumn: { xs: "1 / -1", md: "auto" }, justifySelf: { xs: "stretch", md: "end" } }}>
                {canOpen ? (
                  <Button
                    variant={status === "completed" ? "outlined" : "contained"}
                    fullWidth
                    disabled={Boolean(blocked)}
                    aria-describedby={blocked ? blockedReasonId : undefined}
                    onClick={() => navigate(`/student/resources/${row._id}?resourceType=${typeParam}`)}
                    sx={{ minWidth: 120 }}
                  >
                    {actionLabel(status)}
                  </Button>
                ) : (
                  <Button variant="outlined" fullWidth disabled startIcon={<LockOutlinedIcon />} sx={{ minWidth: 120 }}>
                    Locked
                  </Button>
                )}
              </Box>
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
}
